import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  Bug,
  CheckCircle2,
  HelpCircle,
  Lightbulb,
  Loader2,
  Mail,
  RotateCcw,
  Send,
  type LucideIcon,
} from 'lucide-react'
import { Page } from '@/components/Page'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useToast } from '@/hooks/useToast'
import { ApiError } from '@/lib/api'
import { formatDateTime } from '@/lib/locale'
import {
  useCloseMyContactQuery,
  useMyContactQuery,
  useReopenMyContactQuery,
  useReplyToMyContactQuery,
} from '@/features/contactSales/queries'
import { describeStatus } from '@/features/contactSales/status'
import { orderedPayload } from '@/features/contactSales/payloadOrder'
import { StatusTimeline } from '@/features/contactSales/StatusTimeline'
import type {
  ContactCategory,
  ContactQueryReply,
  ContactQueryRow,
} from '@/features/contactSales/types'

const CATEGORY_META: Record<ContactCategory, { label: string; icon: LucideIcon }> = {
  sales:   { label: 'Talk to Sales',     icon: Mail },
  bug:     { label: 'Report an Issue',   icon: Bug },
  feature: { label: 'Request a Feature', icon: Lightbulb },
  support: { label: 'Get Help',          icon: HelpCircle },
}

const fmtDateTime = (v: string | null) => (v ? formatDateTime(v) : '-')

export function QueryDetailPage() {
  const { id: idParam } = useParams<{ id: string }>()
  const id = Number(idParam)
  const navigate = useNavigate()
  const { toast } = useToast()
  const detailQ = useMyContactQuery(Number.isFinite(id) ? id : null)
  const replyM = useReplyToMyContactQuery()
  const closeM = useCloseMyContactQuery()
  const reopenM = useReopenMyContactQuery()
  const [draft, setDraft] = useState('')

  const message = detailQ.data?.message ?? null
  const replies = detailQ.data?.replies ?? []
  const scrollRef = useRef<HTMLDivElement>(null)

  // Auto-scroll the conversation pane to the latest reply when the
  // thread grows. Runs on first load and on every new bubble (including
  // optimistic ones), so the user always sees what they just said.
  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [replies.length])

  const onToggleClosed = async () => {
    if (!message) return
    try {
      if (message.status === 'closed') {
        await reopenM.mutateAsync(message.id)
        toast({ title: 'Query re-opened.' })
      } else {
        await closeM.mutateAsync(message.id)
        toast({ title: 'Query closed.' })
      }
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Could not update status',
        description: messageFor(err, 'Try again.'),
      })
    }
  }

  const onSendReply = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!message || !draft.trim()) return
    const text = draft.trim()
    setDraft('')
    try {
      await replyM.mutateAsync({ id: message.id, body: text })
    } catch (err) {
      // Restore the user's text so they can retry without retyping.
      setDraft(text)
      toast({
        variant: 'destructive',
        title: 'Reply failed',
        description: messageFor(err, 'Could not send your reply.'),
      })
    }
  }

  return (
    <Page
      title="Query"
      description={message ? CATEGORY_META[message.category].label : undefined}
      width="wide"
      actions={
        <>
          <Button variant="ghost" onClick={() => navigate('/contact-sales/queries')}>
            <ArrowLeft className="size-4" />
            Back to your queries
          </Button>
          {message ? (
            message.status === 'closed' ? (
              <Button
                variant="secondary"
                onClick={onToggleClosed}
                disabled={reopenM.isPending}
              >
                {reopenM.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <RotateCcw className="size-4" />
                )}
                Re-open
              </Button>
            ) : (
              <Button
                variant="secondary"
                onClick={onToggleClosed}
                disabled={closeM.isPending}
              >
                {closeM.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="size-4" />
                )}
                Close query
              </Button>
            )
          ) : null}
        </>
      }
    >
      {detailQ.isLoading ? (
        <Card>
          <CardContent className="py-10">
            <LoadingSpinner />
          </CardContent>
        </Card>
      ) : detailQ.isError || !message ? (
        <Alert variant="destructive">
          <AlertDescription>Could not load this query.</AlertDescription>
        </Alert>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <CardTitle className="flex items-center gap-2">
                    <CategoryIcon category={message.category} />
                    {message.subject || 'Untitled query'}
                  </CardTitle>
                  <StatusBadgeRow message={message} />
                </div>
                <p className="text-xs text-muted-foreground">
                  Submitted {fmtDateTime(message.created_at)} by {message.email}
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {(() => {
                  const ordered = orderedPayload(message.payload, message.category)
                  if (ordered.length === 0) return null
                  return (
                    <div className="grid gap-2 rounded-md border bg-muted/30 p-3 text-xs sm:grid-cols-2">
                      {ordered.map(([k, v]) => (
                        <div key={k}>
                          <span className="font-medium uppercase tracking-wide text-muted-foreground">
                            {prettyKey(k)}:&nbsp;
                          </span>
                          <span className="break-words">{v}</span>
                        </div>
                      ))}
                    </div>
                  )
                })()}
                <p className="whitespace-pre-wrap text-sm">{message.description}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Conversation</CardTitle>
              </CardHeader>
              <CardContent>
                <div
                  ref={scrollRef}
                  className="max-h-[420px] space-y-3 overflow-y-auto pr-1"
                >
                  {replies.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No replies yet. Bokapsys will respond here when they have an update.
                    </p>
                  ) : (
                    replies.map((r) => <ReplyBubble key={r.id} reply={r} />)
                  )}
                </div>
              </CardContent>
            </Card>

            {message.status === 'closed' ? (
              <Alert>
                <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
                  <span>
                    This query is closed. Re-open it to send another reply, or start a new one.
                  </span>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={onToggleClosed}
                    disabled={reopenM.isPending}
                  >
                    {reopenM.isPending ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <RotateCcw className="size-4" />
                    )}
                    Re-open
                  </Button>
                </AlertDescription>
              </Alert>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Reply to Bokapsys</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={onSendReply} className="space-y-3">
                    <Textarea
                      value={draft}
                      onChange={(e) => setDraft(e.currentTarget.value)}
                      placeholder="Add details, share screenshots in writing, or follow up on the team's last reply…"
                      className="min-h-[120px]"
                    />
                    <div className="flex justify-end">
                      <Button type="submit" disabled={replyM.isPending || !draft.trim()}>
                        {replyM.isPending ? (
                          <Loader2 className="size-4 animate-spin" />
                        ) : (
                          <Send className="size-4" />
                        )}
                        Send reply
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}
          </div>

          <aside className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Status</CardTitle>
              </CardHeader>
              <CardContent>
                <StatusTimeline message={message} />
              </CardContent>
            </Card>
          </aside>
        </div>
      )}
    </Page>
  )
}

function CategoryIcon({ category }: { category: ContactCategory }) {
  const Icon = CATEGORY_META[category].icon
  return <Icon className="size-5 text-muted-foreground" />
}

function StatusBadgeRow({ message }: { message: ContactQueryRow }) {
  const desc = describeStatus(message.status, message.category)
  const Icon = desc.icon
  return (
    <Badge variant={desc.badgeVariant} className="gap-1">
      <Icon className="size-3" />
      {desc.label}
    </Badge>
  )
}

function ReplyBubble({ reply }: { reply: ContactQueryReply }) {
  const isStaff = reply.author_type === 'staff'
  // Customer-side perspective: staff replies on the left, customer's own
  // replies on the right. Meta line (name + timestamp) sits UNDER the
  // bubble, edge-aligned with whichever side the bubble is on.
  return (
    <div className={`flex flex-col gap-1 ${isStaff ? 'items-start' : 'items-end'}`}>
      <div
        className={`max-w-[85%] rounded-lg border px-3 py-2 text-sm text-foreground ${
          isStaff ? 'border-primary/20 bg-primary/10' : 'border-border bg-muted'
        }`}
      >
        <p className="whitespace-pre-wrap">{reply.body}</p>
      </div>
      <div className="flex items-center gap-2 px-1 text-[11px]">
        <span className={`font-medium ${isStaff ? 'text-primary' : 'text-foreground'}`}>
          {isStaff ? 'Bokapsys team' : reply.author_name || reply.author_email || 'You'}
        </span>
        <span className="text-muted-foreground">{fmtDateTime(reply.created_at)}</span>
      </div>
    </div>
  )
}

function prettyKey(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim()
}

function messageFor(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}
